import React, { FunctionComponent, RefObject, memo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

import { getDealerBasePath } from "../../../navigation/getDealerBasePath";
import { useCart } from "../../../stores/hooks/useCart";
import { createHashStateHook } from "../../../stores/useHashState";
import {
  PrimaryButton,
  PrimaryOutlinedButton,
} from "../../../ui/Button/Button";
import { Dropdown } from "../../../ui/Dropdown";
import { PriceDisplay } from "../../../ui/PriceDisplay/PriceDisplay";
import { CreatePackageButton } from "../../PackageEditor/CreatePackageButton";
import { useNewQuoteModalState } from "../NewQuoteModal/NewQuoteModal";
import { OrderItemList } from "./OrderItemList";
import { OrderNowButton } from "./OrderNowButton";
import { useShouldRenderOrderNowButton } from "./useShouldRenderOrderNowButton";

type CartPreviewProps = {
  producerRef: RefObject<HTMLButtonElement>;
};

export const useCartDropdownState = createHashStateHook("#cart-preview");

const Uncached: FunctionComponent<CartPreviewProps> = ({ producerRef }) => {
  const { t } = useTranslation();
  const cart = useCart();
  const [, setLocation] = useLocation();
  const [cartDropdownIsVisible, { hide: hideCartPreview }] =
    useCartDropdownState();
  const [, { show: showQuoteModal }] = useNewQuoteModalState();
  const shouldRenderOrderButton = useShouldRenderOrderNowButton();

  const QuoteButton = shouldRenderOrderButton
    ? PrimaryOutlinedButton
    : PrimaryButton;

  return (
    <Dropdown
      aria-labelledby="cart-dropdown-button"
      className="tw-cart-dropdown"
      id="cart-dropdown"
      onClose={hideCartPreview}
      placement="bottom-right"
      producerRef={producerRef}
      role="listbox"
      visible={cartDropdownIsVisible}
    >
      <h2 className="text-2xl font-medium">{t("cart.header.quote")}</h2>
      <OrderItemList cart={cart} />
      {cart && cart.orderItemList.length > 0 && (
        <div className="tw-cart-dropdown__actions">
          <hr className="mb-2 md:mb-4" />
          <div className="grid grid-cols-2 text-xl mb-2">
            <span className="font-light text-ink-2">{t("cart.subtotal")}</span>
            <span className="text-semibold text-right">
              <PriceDisplay>{cart.orderSubtotal}</PriceDisplay>
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1 md:gap-2">
            {shouldRenderOrderButton && (
              <OrderNowButton className="col-span-2 h-14" />
            )}
            <CreatePackageButton
              className="col-span-2"
              id="create-package-button-review"
            />
            <PrimaryOutlinedButton
              aria-label="createNewQuote"
              className="flex h-14 w-full"
              onClick={showQuoteModal}
            >
              {t("cart.createNewQuote")}
            </PrimaryOutlinedButton>
            <QuoteButton
              onClick={(): void => {
                const path = `${getDealerBasePath()}/wishlist/${cart.orderId}`;
                setLocation(path);
                hideCartPreview();
              }}
              aria-label="quoteAction"
              className="flex h-14 w-full"
              type="button"
            >
              {t(
                shouldRenderOrderButton
                  ? "cart.generateQuote"
                  : "cart.viewDetails"
              )}
            </QuoteButton>
          </div>
        </div>
      )}
    </Dropdown>
  );
};

export const CartPreview = memo(Uncached, (prev, next) => {
  return prev.producerRef === next.producerRef;
});
